import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { marker as _ } from '@bling-fe/shared/utils';
import {
  EditorContentLinkTarget,
  EditorContentLinkType
} from '../../content-link-search.token';

const TYPE_ICONS: Record<EditorContentLinkType, string> = {
  document: 'document-text-outline',
  post: 'newspaper-outline',
  event: 'calendar-outline',
  group: 'people-outline'
};

// Tab order is fixed; only types with results get a tab.
const TYPE_ORDER: EditorContentLinkType[] = [
  'document',
  'post',
  'event',
  'group'
];

const TYPE_LABEL_KEYS: Record<EditorContentLinkType, string> = {
  document: _(
    'UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.SECTION_DOCUMENT'
  ),
  post: _('UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.SECTION_POST'),
  event: _('UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.SECTION_EVENT'),
  group: _('UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.SECTION_GROUP')
};

const ALL_TAB = 'all' as const;
type ActiveTab = EditorContentLinkType | typeof ALL_TAB;

@Component({
  selector: 'bl-content-link-dropdown',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule],
  template: `
    <div
      class="content-link-dropdown"
      [style.bottom.px]="position.bottom"
      [style.left.px]="position.left"
    >
      <div class="cl-tabs">
        <button
          type="button"
          class="cl-tab"
          [class.active]="activeTab === 'all'"
          (mousedown)="selectTab('all', $event)"
        >
          {{
            'UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.TAB_ALL'
              | translate
          }}
        </button>
        <button
          *ngFor="let tab of availableTabs"
          type="button"
          class="cl-tab"
          [class.active]="activeTab === tab"
          (mousedown)="selectTab(tab, $event)"
        >
          {{ labelKey(tab) | translate }}
        </button>
      </div>

      <div class="cl-list">
        <button
          type="button"
          class="cl-row"
          *ngFor="let target of visibleTargets; let i = index"
          [class.selected]="i === selectedIndex"
          (mouseenter)="onRowMouseEnter(i)"
          (mousedown)="select(target, $event)"
        >
          <ion-icon
            class="cl-row__icon"
            [name]="iconFor(target.type)"
          ></ion-icon>
          <span class="cl-row__title">{{ target.title }}</span>
          <span
            class="cl-row__meta"
            *ngIf="target.subtitle"
          >
            {{ target.subtitle }}
          </span>
        </button>

        <div
          class="cl-empty"
          *ngIf="loading"
        >
          {{
            'UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.LOADING'
              | translate
          }}
        </div>
        <div
          class="cl-empty"
          *ngIf="!loading && visibleTargets.length === 0"
        >
          {{
            'UI_COMPONENTS.MARKDOWN_EDITOR.CONTENT_LINK_DROPDOWN.NO_RESULTS'
              | translate
          }}
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .content-link-dropdown {
        position: fixed;
        z-index: 10000;
        background: var(--ion-background-color, #fff);
        border: 1px solid var(--ion-color-light, #e0e0e0);
        border-radius: 10px;
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
        width: 460px;
        max-width: 92vw;
        overflow: hidden;
        font-size: 13px;
      }

      .cl-tabs {
        display: flex;
        gap: 4px;
        padding: 8px 12px 0;
        border-bottom: 1px solid var(--ion-color-light, #ececec);
        overflow-x: auto;
      }

      .cl-tab {
        appearance: none;
        background: transparent;
        border: none;
        padding: 8px 12px 10px;
        font-size: 13px;
        font-weight: 400;
        color: var(--ion-color-medium, #8a8a8a);
        cursor: pointer;
        white-space: nowrap;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
      }

      .cl-tab:hover {
        color: var(--ion-text-color, #1a1a1a);
      }

      .cl-tab.active {
        color: var(--ion-text-color, #1a1a1a);
        font-weight: 500;
        border-bottom-color: var(--ion-color-primary, #3171e0);
      }

      .cl-list {
        max-height: 300px;
        overflow-y: auto;
        padding: 6px;
      }

      .cl-row {
        appearance: none;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        text-align: left;
      }

      .cl-row:hover,
      .cl-row.selected {
        background: var(--ion-color-light, #f2f4f7);
      }

      .cl-row__icon {
        font-size: 18px;
        color: var(--ion-color-medium, #8a8a8a);
        flex: 0 0 auto;
      }

      .cl-row__title {
        font-weight: 400;
        color: var(--ion-text-color, #1a1a1a);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cl-row__meta {
        margin-left: auto;
        padding-left: 12px;
        font-size: 12px;
        font-weight: 400;
        color: var(--ion-color-medium, #a0a0a0);
        white-space: nowrap;
        flex: 0 0 auto;
      }

      .cl-empty {
        padding: 14px 12px;
        font-size: 13px;
        color: var(--ion-color-medium, #a0a0a0);
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentLinkDropdownComponent
  implements OnChanges, OnDestroy, AfterViewInit
{
  @Input() anchorRect: DOMRect | null = null;
  @Input() targets: EditorContentLinkTarget[] = [];
  @Input() loading = false;

  @Output() targetSelected = new EventEmitter<EditorContentLinkTarget>();

  selectedIndex = 0;
  activeTab: ActiveTab = ALL_TAB;
  availableTabs: EditorContentLinkType[] = [];

  position = { bottom: 0, left: 0 };

  // Appended to document.body to escape Ionic's `contain: content`, which
  // would re-anchor position:fixed to the containing block (see the
  // richtext editor's mention dropdown for the long version).
  private dropdownElement: HTMLElement | null = null;
  private isAppendedToBody = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  get visibleTargets(): EditorContentLinkTarget[] {
    if (this.activeTab === ALL_TAB) return this.targets;
    return this.targets.filter(t => t.type === this.activeTab);
  }

  iconFor(type: EditorContentLinkType): string {
    return TYPE_ICONS[type];
  }

  labelKey(type: EditorContentLinkType): string {
    return TYPE_LABEL_KEYS[type];
  }

  selectTab(tab: ActiveTab, event?: Event): void {
    // mousedown, not click: keep the editor's selection/caret intact.
    event?.preventDefault();
    this.activeTab = tab;
    this.selectedIndex = 0;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const targets = this.visibleTargets;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          targets.length - 1
        );
        this.cdr.markForCheck();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.cdr.markForCheck();
        break;

      case 'ArrowRight':
      case 'ArrowLeft':
        // Cycle tabs only when the caret isn't needed for text editing —
        // here the menu owns navigation, so switch the active tab.
        event.preventDefault();
        this.cycleTab(event.key === 'ArrowRight' ? 1 : -1);
        break;

      case 'Enter':
      case 'Tab':
        event.preventDefault();
        event.stopImmediatePropagation();
        if (targets[this.selectedIndex]) {
          this.select(targets[this.selectedIndex]);
        }
        break;
    }
  }

  ngAfterViewInit(): void {
    this.appendToBody();
  }

  ngOnDestroy(): void {
    this.removeFromBody();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['anchorRect'] && this.anchorRect) {
      this.updatePosition();
    }

    if (changes['targets']) {
      this.availableTabs = TYPE_ORDER.filter(type =>
        this.targets.some(t => t.type === type)
      );
      if (
        this.activeTab !== ALL_TAB &&
        !this.availableTabs.includes(this.activeTab)
      ) {
        this.activeTab = ALL_TAB;
      }
      this.selectedIndex = 0;
    }
  }

  select(target: EditorContentLinkTarget, event?: Event): void {
    event?.preventDefault();
    this.targetSelected.emit(target);
  }

  // Selection follows the mouse so there is never a second, stale highlight
  // sitting on the first row while the pointer hovers another one.
  onRowMouseEnter(index: number): void {
    if (this.selectedIndex === index) return;
    this.selectedIndex = index;
    this.cdr.markForCheck();
  }

  private cycleTab(direction: 1 | -1): void {
    const tabs: ActiveTab[] = [ALL_TAB, ...this.availableTabs];
    const currentIndex = tabs.indexOf(this.activeTab);
    const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
    this.activeTab = tabs[nextIndex];
    this.selectedIndex = 0;
    this.cdr.markForCheck();
  }

  private appendToBody(): void {
    if (this.isAppendedToBody) return;

    const dropdown = this.elementRef.nativeElement.querySelector(
      '.content-link-dropdown'
    );
    if (dropdown) {
      this.dropdownElement = dropdown;
      this.renderer.appendChild(document.body, dropdown);
      this.isAppendedToBody = true;
    }
  }

  private removeFromBody(): void {
    if (this.dropdownElement && this.isAppendedToBody) {
      try {
        this.renderer.removeChild(document.body, this.dropdownElement);
      } catch {
        // Element might already be removed
      }
      this.isAppendedToBody = false;
      this.dropdownElement = null;
    }
  }

  private updatePosition(): void {
    if (!this.anchorRect) {
      return;
    }

    const dropdownWidth = 460;
    const viewportWidth = window.innerWidth;

    const bottom = window.innerHeight - this.anchorRect.top + 6;

    let left = this.anchorRect.left;
    if (left + dropdownWidth > viewportWidth) {
      left = Math.max(8, viewportWidth - dropdownWidth - 8);
    }

    this.position = { bottom, left };
  }
}
